import React from 'react';
import { Button } from '@/components/ui/button';
import { ChevronDown, Waves, Sparkles } from 'lucide-react';
import { motion } from 'framer-motion';

const Hero = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.8, ease: "easeOut" }
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  return (
    <section id="inicio" className="relative min-h-screen flex items-center justify-center overflow-hidden bg-deep-50">
      {/* Background Image with Dark Film Overlay */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url('/images/hero3.jpg')`
        }}
      />
      {/* Dark film overlay for contrast - Tema Oscuro */}
      <div className="absolute inset-0 bg-deep-50/80"></div>
      {/* Blue-black gradient overlay for dark theme */}
      <div 
        className="absolute inset-0"
        style={{
          background: `linear-gradient(135deg, rgba(0, 0, 0, 0.7) 0%, rgba(10, 25, 41, 0.8) 50%, rgba(10, 31, 31, 0.7) 100%)`
        }}
      />
      
      {/* Animated Background Elements - Dark Theme */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div 
          className="absolute top-1/4 left-1/4 w-64 h-64 bg-ocean-500/15 rounded-full blur-3xl"
          animate={{ 
            scale: [1, 1.2, 1], 
            opacity: [0.15, 0.3, 0.15] 
          }}
          transition={{ 
            duration: 8, 
            repeat: Infinity, 
            ease: "easeInOut" 
          }}
        />
        <motion.div 
          className="absolute top-3/4 right-1/4 w-96 h-96 bg-jade-500/10 rounded-full blur-3xl"
          animate={{ 
            scale: [1, 1.1, 1], 
            opacity: [0.1, 0.25, 0.1] 
          }}
          transition={{ 
            duration: 10, 
            repeat: Infinity, 
            ease: "easeInOut",
            delay: 2 
          }}
        />
      </div>

      {/* Content */}
      <motion.div 
        className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center"
        variants={staggerContainer}
        initial="initial"
        animate="animate"
      >
        
        <motion.div 
          className="mb-8 space-y-6"
          variants={staggerContainer}
        >
          {/* Badge - Dark Theme */}
          <motion.div 
            className="inline-flex items-center gap-2 px-4 py-2 bg-deep-200/60 backdrop-blur-sm border border-ocean-500/40 rounded-full text-pearl-900 text-sm font-medium shadow-lg"
            variants={fadeInUp}
            whileHover={{ scale: 1.05, y: -2 }}
            transition={{ type: "spring", stiffness: 300 }}
          >
            <motion.div
              animate={{ rotate: [0, 15, -15, 0] }}
              transition={{ duration: 2, repeat: Infinity, ease: [0.4, 0, 0.2, 1] }}
            >
              <Waves className="w-4 h-4 text-ocean-600" />
            </motion.div>
            <span className="font-body">Primer Oyster Bar Móvil de Argentina</span>
          </motion.div>

          {/* Traditional Hero Title - Dark Theme con azul brillante */}
          <motion.h1 
            className="text-5xl md:text-7xl font-title font-semibold mb-6 leading-tight tracking-wide"
            variants={fadeInUp}
          >
            <motion.span 
              className="block text-ocean-600"
              animate={{
                scale: [1, 1.01, 1]
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                ease: [0.4, 0, 0.2, 1]
              }}
            >
              Mar de Jade
            </motion.span>
            <motion.span 
              className="block text-ocean-600"
              animate={{
                scale: [1, 1.01, 1]
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                ease: [0.4, 0, 0.2, 1]
              }}
            >
              Hogar
            </motion.span>
          </motion.h1>
          
          {/* Main value proposition - Dark Theme */}
          <motion.p 
            className="text-2xl md:text-3xl text-pearl-800 mb-4 max-w-4xl mx-auto font-bold font-body"
            variants={fadeInUp}
          >
            Mariscos vivos del sur
          </motion.p>

          {/* Oyster Bar Experience - Dark Theme */}
          <motion.div 
            className="mb-8 max-w-3xl mx-auto"
            variants={fadeInUp}
          >
            <motion.a
              href="#servicios"
              className="group relative inline-block cursor-pointer"
              whileHover={{ 
                scale: 1.02,
                y: -1
              }}
              whileTap={{ scale: 0.98 }}
            >
              <motion.div 
                className="text-lg md:text-xl text-pearl-700 font-body font-medium flex items-center justify-center gap-2 px-4 py-2 rounded-full group-hover:text-ocean-600 transition-all duration-300 relative z-10"
                animate={{ 
                  opacity: [0.8, 1, 0.8]
                }}
                transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
              >
                <motion.span 
                  className="text-lg transition-transform duration-300 group-hover:scale-110"
                  animate={{ scale: [1, 1.05, 1] }}
                  transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                >
                  🦪
                </motion.span>
                <span className="relative">
                  Experiencia Oyster Bar en eventos únicos
                  <span 
                    className="absolute bottom-0 left-0 h-0.5 bg-gradient-to-r from-ocean-500 to-jade-500 rounded-full transition-all duration-300 w-0 group-hover:w-full"
                  />
                </span>
              </motion.div>
            </motion.a>
          </motion.div>

          {/* Tagline - Dark Theme */}
          <motion.p 
            className="text-lg md:text-xl text-pearl-600 mb-8 max-w-2xl mx-auto leading-relaxed font-body italic font-medium"
            variants={fadeInUp}
          >
            Del mar, sin escalas
          </motion.p>
        </motion.div>
        
        {/* CTA Buttons - Dark Theme */}
        <motion.div 
          className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8"
          variants={fadeInUp}
        >
          <motion.div
            whileHover={{ scale: 1.05, y: -5 }}
            whileTap={{ scale: 0.95 }}
          >
            <Button 
              size="lg" 
              className="group px-8 py-4 text-lg bg-ocean-500 hover:bg-ocean-600 text-white shadow-2xl hover:shadow-ocean-500/40 transition-all duration-500 font-body font-semibold rounded-2xl border border-ocean-400/30"
            >
              <motion.div
                animate={{ rotate: [0, 15, -15, 0] }}
                transition={{ duration: 2, repeat: Infinity, ease: [0.4, 0, 0.2, 1] }}
              >
                <Waves className="w-5 h-5 mr-2" />
              </motion.div>
              Ver Productos
            </Button>
          </motion.div>
          
          <motion.div
            whileHover={{ scale: 1.05, y: -5 }}
            whileTap={{ scale: 0.95 }}
          >
            <Button 
              variant="outline" 
              size="lg" 
              className="px-8 py-4 text-lg border-2 border-ocean-500 text-ocean-600 hover:bg-ocean-500 hover:text-white transition-all duration-500 font-body font-semibold rounded-2xl shadow-lg hover:shadow-ocean-500/30 backdrop-blur-sm bg-deep-200/40"
            >
              <Sparkles className="w-5 h-5 mr-2" />
              Oyster Bar Móvil
            </Button>
          </motion.div>
        </motion.div>
      </motion.div>
      
      {/* Modern Scroll Indicator - Dark Theme */}
      <div className="hidden md:block absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <div className="flex flex-col items-center gap-2">
          <span className="text-pearl-600 text-sm font-medium">Descubrí más</span>
          <div className="w-6 h-10 border-2 border-ocean-500/70 rounded-full flex justify-center relative overflow-hidden">
            <div className="w-1 h-3 bg-ocean-500 rounded-full mt-2 animate-pulse" />
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-ocean-500/20 to-transparent animate-pulse" />
          </div>
          <ChevronDown className="w-5 h-5 text-ocean-500 animate-bounce delay-300" />
        </div>
      </div>

      {/* Floating Elements - Dark Theme */}
      <div className="absolute top-20 left-10 w-3 h-3 bg-ocean-500 rounded-full animate-float opacity-40" />
      <div className="absolute top-40 right-20 w-2 h-2 bg-jade-500 rounded-full animate-float delay-1000 opacity-30" />
      <div className="absolute bottom-32 left-20 w-4 h-4 bg-ocean-600 rounded-full animate-float delay-2000 opacity-35" />

      {/* Efecto oxigenador de pecera - Burbujas Dark Theme */}
      <motion.div className="absolute bottom-8 right-16 z-5">
        {/* Burbuja grande 1 */}
        <motion.div 
          className="absolute w-3 h-3 bg-ocean-500/40 rounded-full shadow-lg border border-ocean-400/30"
          animate={{
            y: [0, -120, -240, -360, -480],
            x: [0, -5, 3, -8, 5],
            scale: [0.8, 1, 1.2, 1.4, 0.6],
            opacity: [0, 0.8, 0.9, 0.7, 0]
          }}
          transition={{
            duration: 6,
            repeat: Infinity,
            ease: "easeOut",
            delay: 0
          }}
        />

        {/* Burbuja grande 2 */}
        <motion.div 
          className="absolute w-2.5 h-2.5 bg-jade-500/30 rounded-full shadow-md border border-jade-400/40"
          animate={{
            y: [0, -140, -280, -420, -560],
            x: [0, 8, -3, 12, -6],
            scale: [0.7, 1.1, 1.3, 1.5, 0.4],
            opacity: [0, 0.7, 0.9, 0.6, 0]
          }}
          transition={{
            duration: 7,
            repeat: Infinity,
            ease: "easeOut",
            delay: 0.5
          }}
        />

        {/* Burbuja mediana 1 */}
        <motion.div 
          className="absolute w-2 h-2 bg-ocean-600/35 rounded-full shadow-md border border-ocean-500/30"
          animate={{
            y: [0, -100, -200, -300, -400],
            x: [0, -3, 6, -9, 4],
            scale: [0.6, 0.9, 1.1, 1.2, 0.3],
            opacity: [0, 0.8, 0.9, 0.5, 0]
          }}
          transition={{
            duration: 5.5,
            repeat: Infinity,
            ease: "easeOut",
            delay: 1
          }}
        />

        {/* Burbuja mediana 2 */}
        <motion.div 
          className="absolute w-1.5 h-1.5 bg-jade-600/40 rounded-full shadow-sm border border-jade-500/30"
          animate={{
            y: [0, -110, -220, -330, -440],
            x: [0, 7, -4, 10, -5],
            scale: [0.5, 0.8, 1, 1.1, 0.2],
            opacity: [0, 0.9, 0.8, 0.4, 0]
          }}
          transition={{
            duration: 5.8,
            repeat: Infinity,
            ease: "easeOut",
            delay: 1.3
          }}
        />

        {/* Micro-burbujas rápidas */}
        <motion.div 
          className="absolute w-1 h-1 bg-ocean-500/45 rounded-full shadow-sm"
          animate={{
            y: [0, -80, -160, -240, -320],
            x: [0, -2, 4, -6, 3],
            scale: [0.4, 0.7, 0.9, 0.8, 0.1],
            opacity: [0, 1, 0.9, 0.3, 0]
          }}
          transition={{
            duration: 4.5,
            repeat: Infinity,
            ease: "easeOut",
            delay: 0.2
          }}
        />

        <motion.div 
          className="absolute w-1 h-1 bg-jade-500/40 rounded-full shadow-sm"
          animate={{
            y: [0, -90, -180, -270, -360],
            x: [0, 5, -2, 8, -4],
            scale: [0.3, 0.6, 0.8, 0.7, 0.1],
            opacity: [0, 0.9, 0.8, 0.4, 0]
          }}
          transition={{
            duration: 4.8,
            repeat: Infinity,
            ease: "easeOut",
            delay: 0.7
          }}
        />

        <motion.div 
          className="absolute w-0.5 h-0.5 bg-ocean-600/50 rounded-full"
          animate={{
            y: [0, -70, -140, -210, -280],
            x: [0, -1, 3, -4, 2],
            scale: [0.2, 0.5, 0.7, 0.6, 0.1],
            opacity: [0, 1, 0.7, 0.3, 0]
          }}
          transition={{
            duration: 4.2,
            repeat: Infinity,
            ease: "easeOut",
            delay: 1.1
          }}
        />

        {/* Burbujas extras para densidad */}
        <motion.div 
          className="absolute w-1.5 h-1.5 bg-jade-600/35 rounded-full shadow-sm border border-jade-500/25"
          animate={{
            y: [0, -130, -260, -390, -520],
            x: [0, 6, -2, 9, -7],
            scale: [0.5, 0.9, 1.2, 1.3, 0.2],
            opacity: [0, 0.6, 0.8, 0.4, 0]
          }}
          transition={{
            duration: 6.2,
            repeat: Infinity,
            ease: "easeOut",
            delay: 1.8
          }}
        />

        <motion.div 
          className="absolute w-0.5 h-0.5 bg-ocean-500/40 rounded-full"
          animate={{
            y: [0, -60, -120, -180, -240],
            x: [0, 2, -1, 4, -3],
            scale: [0.3, 0.6, 0.8, 0.5, 0.1],
            opacity: [0, 0.9, 0.6, 0.2, 0]
          }}
          transition={{
            duration: 3.8,
            repeat: Infinity,
            ease: "easeOut",
            delay: 2.5
          }}
        />
      </motion.div>
    </section>
  );
};

export default Hero;
